import { createSlice } from '@reduxjs/toolkit'
import { createAsyncThunk } from '@reduxjs/toolkit'

import GroupService from 'services/GroupService'

export const initialState = {
    groupList: {
        data: [],
        isLoading: false,
        error: false,
    },
    groupListDynamic: {
        data: [],
        isLoading: false,
        error: false,
    },
    groupDetail: {
        data: [],
        isLoading: false,
        error: false,
    },
    groupAdd: {
        data: [],
        isLoading: false,
        error: false,
        success: null,
    },
    groupEdit: {
        data: [],
        isLoading: false,
        error: false,
        success: null,
    },
    groupChargePoints: {
        data: [],
        isLoading: false,
        error: false,
    },
    removeCommand: {
        isLoading: false,
        error: false,
        success: false,
    },
    retryCommand: {
        isLoading: false,
        error: false,
        success: false,
    },
    retryCommandAll: {
        isLoading: false,
        error: false,
        success: false,
    },
    archiveCommand: {
        isLoading: false,
        error: false,
        success: false,
    },
    unarchiveCommand: {
        isLoading: false,
        error: false,
        success: false,
    },
    activeCommands: {
        data: [],
        isLoading: false,
        error: false,
    },
    archivedCommands: {
        data: [],
        isLoading: false,
        error: false,
    },
}

export const fetchGroupList = createAsyncThunk('group/getGroupList', async (data, { rejectWithValue }) => {
    try {
        const response = await GroupService.getGroupList()
        return [...response.data]
    } catch (err) {
        return rejectWithValue(err.response?.data?.message || 'Error')
    }
})

export const fetchGroupListDynamic = createAsyncThunk(
    'group/getGroupListDynamic',
    async (data, { rejectWithValue }) => {
        try {
            const response = await GroupService.getGroupListDynamic(data)
            return response.data
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || 'Error')
        }
    }
)

export const fetchGroupsToExcel = createAsyncThunk('group/getGroupsExcel', async (data, { rejectWithValue }) => {
    try {
        const response = await GroupService.getGroupsToExcel(data)
        return response
    } catch (err) {
        return rejectWithValue(err.response?.data?.message || 'Error')
    }
})

export const fetchGroupDetail = createAsyncThunk('group/getGroupDetail', async (data, { rejectWithValue }) => {
    try {
        const response = await GroupService.getGroupDetail(data)
        return response.data
    } catch (err) {
        return rejectWithValue(err.response?.data?.message || 'Error')
    }
})

export const addGroup = createAsyncThunk('group/addGroup', async (data, { rejectWithValue }) => {
    try {
        const response = await GroupService.addGroup(data)
        return [response]
    } catch (err) {
        return rejectWithValue(err.response?.error || 'Error')
    }
})

export const deleteGroup = createAsyncThunk('group/deleteGroup', async (params, { rejectWithValue }) => {
    try {
        await GroupService.deleteGroup(params)
        return params
    } catch (err) {
        return rejectWithValue(err.response?.data?.error || 'Error')
    }
})

export const updateGroup = createAsyncThunk('group/updateGroup', async (data, { rejectWithValue }) => {
    try {
        const response = await GroupService.updateGroup(data)
        return response
    } catch (err) {
        return rejectWithValue(err.response?.data?.error || 'Error')
    }
})

export const fetchGroupChargePoints = createAsyncThunk(
    'group/getGroupChargePoints',
    async (groupId, { rejectWithValue }) => {
        try {
            const response = await GroupService.getGroupChargePoints(groupId)
            return response.data
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || 'Error')
        }
    }
)

export const updateGroupChargePoints = createAsyncThunk(
    'group/updateGroupChargePoints',
    async (data, { rejectWithValue }) => {
        try {
            const response = await GroupService.updateGroupChargePoints(data)
            return response.data
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || 'Error')
        }
    }
)

export const removeCommand = createAsyncThunk('group/removeCommand', async (commandId, { rejectWithValue }) => {
    try {
        await GroupService.removeCommand(commandId)
        return commandId
    } catch (err) {
        return rejectWithValue(err.response?.data?.message || 'Error')
    }
})

export const retryCommand = createAsyncThunk('group/retryCommand', async (commandDetailId, { rejectWithValue }) => {
    try {
        const response = await GroupService.retryCommand(commandDetailId)
        return response.data
    } catch (err) {
        return rejectWithValue(err.response?.data?.message || 'Error')
    }
})

export const retryCommandAll = createAsyncThunk('group/retryCommandAll', async (commandId, { rejectWithValue }) => {
    try {
        const response = await GroupService.retryCommandAll(commandId)
        return response.data
    } catch (err) {
        return rejectWithValue(err.response?.data?.message || 'Error')
    }
})

export const archiveCommand = createAsyncThunk('group/archiveCommand', async (commandId, { rejectWithValue }) => {
    try {
        const response = await GroupService.archiveCommand(commandId)
        return response.data
    } catch (err) {
        return rejectWithValue(err.response?.data?.message || 'Error')
    }
})

export const unarchiveCommand = createAsyncThunk('group/unarchiveCommand', async (commandId, { rejectWithValue }) => {
    try {
        const response = await GroupService.unarchiveCommand(commandId)
        return response.data
    } catch (err) {
        return rejectWithValue(err.response?.data?.message || 'Error')
    }
})

export const fetchActiveCommands = createAsyncThunk(
    'group/fetchActiveCommands',
    async (params, { rejectWithValue }) => {
        try {
            const response = await GroupService.getActiveCommands(params)
            return response.data
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || 'Error')
        }
    }
)

export const fetchArchivedCommands = createAsyncThunk(
    'group/fetchArchivedCommands',
    async (params, { rejectWithValue }) => {
        try {
            const response = await GroupService.getArchivedCommands(params)
            return response.data
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || 'Error')
        }
    }
)

export const groupSlice = createSlice({
    name: 'group',
    initialState,
    reducers: {
        updateAddGroupStatus(state, action) {
            state.addGroupStatus = action.payload
        },
        resetGroupChargePoints(state) {
            state.groupChargePoints.data = []
            state.groupChargePoints.isLoading = false
            state.groupChargePoints.error = false
        },
    },
    extraReducers(builder) {
        builder
            // List Groups
            .addCase(fetchGroupList.pending, (state, action) => {
                state.groupList.isLoading = true
            })
            .addCase(fetchGroupList.rejected, (state, action) => {
                state.groupList.isLoading = false
                state.groupList.error = true
            })
            .addCase(fetchGroupList.fulfilled, (state, action) => {
                state.groupList.isLoading = false
                state.groupList.error = false
                state.groupList.data = action.payload
            })

            // List Groups Dynamic
            .addCase(fetchGroupListDynamic.pending, (state, action) => {
                state.groupListDynamic.isLoading = true
            })
            .addCase(fetchGroupListDynamic.rejected, (state, action) => {
                state.groupListDynamic.isLoading = false
                state.groupListDynamic.error = true
            })
            .addCase(fetchGroupListDynamic.fulfilled, (state, action) => {
                state.groupListDynamic.isLoading = false
                state.groupListDynamic.error = false
                state.groupListDynamic.data = action.payload
            })

            // Get Group by ID
            .addCase(fetchGroupDetail.pending, (state, action) => {
                state.groupDetail.isLoading = true
            })
            .addCase(fetchGroupDetail.rejected, (state, action) => {
                state.groupDetail.isLoading = false
                state.groupDetail.error = action.payload || 'Error loading group details'
            })
            .addCase(fetchGroupDetail.fulfilled, (state, action) => {
                state.groupDetail.isLoading = false
                state.groupDetail.error = false
                state.groupDetail.data = action.payload
            })

            // Add Group
            .addCase(addGroup.pending, (state, action) => {
                state.groupAdd.isLoading = true
            })
            .addCase(addGroup.rejected, (state, action) => {
                state.groupAdd.isLoading = false
                state.groupAdd.error = true
            })
            .addCase(addGroup.fulfilled, (state, action) => {
                state.groupAdd.isLoading = false
                state.groupAdd.error = false
                state.groupAdd.success = true
                state.groupAdd.data = [...state.groupList.data, action.payload]
            })

            .addCase(deleteGroup.fulfilled, (state, action) => {
                state.groupList.data = state.groupList.data.filter((group) => group.id !== action.payload)
            })

            // Edit group
            .addCase(updateGroup.pending, (state, action) => {
                state.groupEdit.isLoading = true
            })
            .addCase(updateGroup.rejected, (state, action) => {
                state.groupEdit.isLoading = false
                state.groupEdit.error = action.error.message
            })
            .addCase(updateGroup.fulfilled, (state, action) => {
                state.groupEdit.isLoading = false
                state.groupEdit.error = false
                state.groupEdit.success = true
                // Update the group in the list
                const updatedGroup = action.payload
                const index = state.groupList.data.findIndex((group) => group.id === updatedGroup.id)
                if (index !== -1) {
                    state.groupList.data[index] = updatedGroup
                }
            })

            // Group Charge Points
            .addCase(fetchGroupChargePoints.pending, (state, action) => {
                state.groupChargePoints.isLoading = true
            })
            .addCase(fetchGroupChargePoints.rejected, (state, action) => {
                state.groupChargePoints.isLoading = false
                state.groupChargePoints.error = action.payload || 'Error loading group charge points'
            })
            .addCase(fetchGroupChargePoints.fulfilled, (state, action) => {
                state.groupChargePoints.isLoading = false
                state.groupChargePoints.error = false
                state.groupChargePoints.data = action.payload
            })

            // Update Group Charge Points
            .addCase(updateGroupChargePoints.pending, (state, action) => {
                state.groupChargePoints.isLoading = true
            })
            .addCase(updateGroupChargePoints.rejected, (state, action) => {
                state.groupChargePoints.isLoading = false
                state.groupChargePoints.error = action.payload || 'Error updating group charge points'
            })
            .addCase(updateGroupChargePoints.fulfilled, (state, action) => {
                state.groupChargePoints.isLoading = false
                state.groupChargePoints.error = false
                state.groupChargePoints.data = action.payload
            })

            // Remove Command
            .addCase(removeCommand.pending, (state, action) => {
                state.removeCommand.isLoading = true
                state.removeCommand.error = false
                state.removeCommand.success = false
            })
            .addCase(removeCommand.rejected, (state, action) => {
                state.removeCommand.isLoading = false
                state.removeCommand.error = action.payload || 'Error removing command'
                state.removeCommand.success = false
            })
            .addCase(removeCommand.fulfilled, (state, action) => {
                state.removeCommand.isLoading = false
                state.removeCommand.error = false
                state.removeCommand.success = true
            })

            // Retry Command
            .addCase(retryCommand.pending, (state, action) => {
                state.retryCommand.isLoading = true
                state.retryCommand.error = false
                state.retryCommand.success = false
            })
            .addCase(retryCommand.rejected, (state, action) => {
                state.retryCommand.isLoading = false
                state.retryCommand.error = action.payload || 'Error retrying command'
                state.retryCommand.success = false
            })
            .addCase(retryCommand.fulfilled, (state, action) => {
                state.retryCommand.isLoading = false
                state.retryCommand.error = false
                state.retryCommand.success = true
            })

            // Retry Command All
            .addCase(retryCommandAll.pending, (state, action) => {
                state.retryCommandAll.isLoading = true
                state.retryCommandAll.error = false
                state.retryCommandAll.success = false
            })
            .addCase(retryCommandAll.rejected, (state, action) => {
                state.retryCommandAll.isLoading = false
                state.retryCommandAll.error = action.payload || 'Error retrying all commands'
                state.retryCommandAll.success = false
            })
            .addCase(retryCommandAll.fulfilled, (state, action) => {
                state.retryCommandAll.isLoading = false
                state.retryCommandAll.error = false
                state.retryCommandAll.success = true
            })

            // Archive Command
            .addCase(archiveCommand.pending, (state, action) => {
                state.archiveCommand.isLoading = true
                state.archiveCommand.error = false
                state.archiveCommand.success = false
            })
            .addCase(archiveCommand.rejected, (state, action) => {
                state.archiveCommand.isLoading = false
                state.archiveCommand.error = action.payload || 'Error archiving command'
                state.archiveCommand.success = false
            })
            .addCase(archiveCommand.fulfilled, (state, action) => {
                state.archiveCommand.isLoading = false
                state.archiveCommand.error = false
                state.archiveCommand.success = true
            })

            // Unarchive Command
            .addCase(unarchiveCommand.pending, (state, action) => {
                state.unarchiveCommand.isLoading = true
                state.unarchiveCommand.error = false
                state.unarchiveCommand.success = false
            })
            .addCase(unarchiveCommand.rejected, (state, action) => {
                state.unarchiveCommand.isLoading = false
                state.unarchiveCommand.error = action.payload || 'Error unarchiving command'
                state.unarchiveCommand.success = false
            })
            .addCase(unarchiveCommand.fulfilled, (state, action) => {
                state.unarchiveCommand.isLoading = false
                state.unarchiveCommand.error = false
                state.unarchiveCommand.success = true
            })

            // Fetch Active Commands
            .addCase(fetchActiveCommands.pending, (state, action) => {
                state.activeCommands.isLoading = true
                state.activeCommands.error = false
            })
            .addCase(fetchActiveCommands.rejected, (state, action) => {
                state.activeCommands.isLoading = false
                state.activeCommands.error = action.payload || 'Error loading active commands'
            })
            .addCase(fetchActiveCommands.fulfilled, (state, action) => {
                state.activeCommands.isLoading = false
                state.activeCommands.error = false
                state.activeCommands.data = action.payload
            })

            // Fetch Archived Commands
            .addCase(fetchArchivedCommands.pending, (state, action) => {
                state.archivedCommands.isLoading = true
                state.archivedCommands.error = false
            })
            .addCase(fetchArchivedCommands.rejected, (state, action) => {
                state.archivedCommands.isLoading = false
                state.archivedCommands.error = action.payload || 'Error loading archived commands'
            })
            .addCase(fetchArchivedCommands.fulfilled, (state, action) => {
                state.archivedCommands.isLoading = false
                state.archivedCommands.error = false
                state.archivedCommands.data = action.payload
            })
    },
})

export const getGroupList = (state) => state.group.groupList
export const getGroupListStatus = (state) => state.group.getGroupListStatus
export const getGroupListError = (state) => state.group.getGroupListError

export const addGroupStatus = (state) => state.group.addGroupStatus
export const addGroupError = (state) => state.group.addGroupError

export const getRemoveCommandStatus = (state) => state.group.removeCommand
export const getRetryCommandStatus = (state) => state.group.retryCommand
export const getRetryCommandAllStatus = (state) => state.group.retryCommandAll
export const getArchiveCommandStatus = (state) => state.group.archiveCommand
export const getUnarchiveCommandStatus = (state) => state.group.unarchiveCommand
export const getActiveCommands = (state) => state.group.activeCommands
export const getArchivedCommands = (state) => state.group.archivedCommands

export const { updateAddGroupStatus, resetGroupChargePoints } = groupSlice.actions

export default groupSlice.reducer
